/**
 * Smoothr Pay (Adyen for Platforms).
 *
 * Values mirror the smoothr-pay-onboarding-service `openapi.yaml` and the new
 * backoffice's `src/modules/smoothrPay/types.ts` — the two UIs talk to the same
 * service, so a value that differs here is a value the backend will reject.
 */

/** Sales channel a business line serves. */
export enum MerchantChannel {
  ECOM = 'ecom',
  POS = 'pos',
}

/** Lifecycle of a merchant configuration. */
export enum MerchantConfigurationStatus {
  DRAFT = 'draft',
  INVITED = 'invited',
  ONBOARDING = 'onboarding',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  CLOSED = 'closed',
}

/** Adyen verification state of a single capability. */
export enum CapabilityVerificationStatus {
  PENDING = 'pending',
  VALID = 'valid',
  INVALID = 'invalid',
  REJECTED = 'rejected',
}

/** Whether the onboarding invitation mail was sent / acted on. */
export enum InvitationStatus {
  NOT_SENT = 'not_sent',
  SENT = 'sent',
  COMPLETED = 'completed',
  EXPIRED = 'expired',
}

/** Payout bank account state. */
export enum TransferInstrumentStatus {
  VALID = 'valid',
  PENDING = 'pending',
  INVALID = 'invalid',
}

/** Lifecycle of the Adyen store a venue is mapped to. */
export enum VenueStoreStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  CLOSED = 'closed',
}

export enum VenuePaymentMethodStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  DISABLED = 'disabled',
}

/** Payment methods Adyen can be asked to enable on a store. */
export enum AdyenPaymentMethodType {
  VISA = 'visa',
  MASTERCARD = 'mc',
  GOOGLE_PAY = 'googlepay',
  APPLE_PAY = 'applepay',
  AMEX = 'amex',
  GIROCARD = 'girocard',
  /**
   * Not offered — Smoothr Pay does not support PayPal. Kept because the service
   * still returns it, so a store that had one enabled before still reads back
   * with its proper name and can be disabled.
   */
  PAYPAL = 'paypal',
}

/** Amex contract level. The service defaults to `noContract` when omitted. */
export enum AdyenAmexServiceLevel {
  NO_CONTRACT = 'noContract',
  GATEWAY_CONTRACT = 'gatewayContract',
  PAYMENT_DESIGNATOR_CONTRACT = 'paymentDesignatorContract',
}

/**
 * Adyen "platform experience" UI components the backoffice can embed. The
 * session's granted roles are derived from these server-side, so asking for
 * fewer components narrows the token.
 */
export enum AdyenPlatformComponent {
  TRANSACTIONS_OVERVIEW = 'transactionsOverview',
  PAYOUTS_OVERVIEW = 'payoutsOverview',
}

/**
 * The Adyen provisioning steps, in the order the service requires them. `KYC` is
 * not a provisioning call — it hands the merchant off to Adyen's hosted
 * onboarding — but it is the last thing the operator triggers, so it belongs in
 * the same sequence.
 */
export enum ProvisioningStep {
  LEGAL_ENTITY = 'legalEntity',
  BUSINESS_LINES = 'businessLines',
  ACCOUNT_HOLDER = 'accountHolder',
  BALANCE_ACCOUNTS = 'balanceAccounts',
  KYC = 'kyc',
}
